/**
 * User Provisioning API Client
 * ISO 27001 A.9.2.1 - User registration and de-registration
 */

import { fetchWithAuth } from '@/utils/api';

export type UserRole = 'SUPERADMIN' | 'OWNER' | 'ADMIN' | 'MEMBER' | 'TECHNICIAN' | 'ACCOUNTANT' | 'READONLY' | 'EXTERNAL_API';
export type ProvisioningStatus = 'PENDING' | 'APPROVED_L1' | 'APPROVED' | 'REJECTED' | 'EXPIRED' | 'CANCELLED';

export interface ProvisioningRequestInput {
  targetEmail: string;
  targetFirstName?: string;
  targetLastName?: string;
  requestedRole: UserRole;
  requestedCompanies: string[];
  justification: string;
}

export interface ProvisioningRequest {
  id: string;
  tenantId: string;
  requesterId: string;
  targetEmail: string;
  targetFirstName?: string | null;
  targetLastName?: string | null;
  requestedRole: UserRole;
  requestedCompanies: string[];
  justification: string;
  status: ProvisioningStatus;
  approver1Id?: string | null;
  approver1Date?: string | null;
  approver1Comment?: string | null;
  approver2Id?: string | null;
  approver2Date?: string | null;
  approver2Comment?: string | null;
  rejectedById?: string | null;
  rejectionReason?: string | null;
  rejectedAt?: string | null;
  expiresAt: string;
  createdUserId?: string | null;
  processedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  requiresDoubleApproval?: boolean;
  canApprove?: boolean;
  Requester?: { id: string; email: string; fullName?: string | null };
  Approver1?: { id: string; email: string; fullName?: string | null } | null;
  Approver2?: { id: string; email: string; fullName?: string | null } | null;
  RejectedBy?: { id: string; email: string; fullName?: string | null } | null;
  CreatedUser?: { id: string; email: string; fullName?: string | null } | null;
}

export interface ProvisioningStats {
  pending: number;
  approved: number;
  rejected: number;
  expired: number;
  total: number;
}

export interface ApprovalResult {
  status: 'APPROVED' | 'APPROVED_L1';
  message: string;
  createdUser?: {
    id: string;
    email: string;
    fullName?: string | null;
    role: string;
  };
}

export interface RejectionResult {
  status: 'REJECTED';
  message: string;
}

const API_BASE = '/api/v1/core';

export const userProvisioningApi = {
  /**
   * Create a new provisioning request
   */
  async createRequest(data: ProvisioningRequestInput): Promise<ProvisioningRequest> {
    const response = await fetchWithAuth(`${API_BASE}/user-provisioning/requests`, {
      method: 'POST',

      body: JSON.stringify(data),
    });
    const result = await response.json();
    return result.data;
  },

  /**
   * Get all requests (admin view)
   */
  async getAllRequests(options?: {
    status?: ProvisioningStatus;
    requesterId?: string;
  }): Promise<ProvisioningRequest[]> {
    const params = new URLSearchParams();
    if (options?.status) params.append('status', options.status);
    if (options?.requesterId) params.append('requesterId', options.requesterId);
    const queryString = params.toString() ? `?${params.toString()}` : '';

    const response = await fetchWithAuth(`${API_BASE}/user-provisioning/requests${queryString}`);
    const result = await response.json();
    return result.data;
  },

  /**
   * Get my requests (as requester)
   */
  async getMyRequests(): Promise<ProvisioningRequest[]> {
    const response = await fetchWithAuth(`${API_BASE}/user-provisioning/my-requests`);
    const result = await response.json();
    return result.data;
  },

  /**
   * Get pending requests for approval
   */
  async getPendingRequests(): Promise<ProvisioningRequest[]> {
    const response = await fetchWithAuth(`${API_BASE}/user-provisioning/pending`);
    const result = await response.json();
    return result.data;
  },

  /**
   * Get a specific request
   */
  async getRequest(id: string): Promise<ProvisioningRequest> {
    const response = await fetchWithAuth(`${API_BASE}/user-provisioning/requests/${id}`);
    const result = await response.json();
    return result.data;
  },

  /**
   * Get statistics
   */
  async getStats(): Promise<ProvisioningStats> {
    const response = await fetchWithAuth(`${API_BASE}/user-provisioning/stats`);
    const result = await response.json();
    return result.data;
  },

  /**
   * Approve a request
   */
  async approveRequest(id: string, comment?: string): Promise<ApprovalResult> {
    const response = await fetchWithAuth(`${API_BASE}/user-provisioning/requests/${id}/approve`, {
      method: 'POST',

      body: JSON.stringify({ comment }),
    });
    const result = await response.json();
    return result.data;
  },

  /**
   * Reject a request
   */
  async rejectRequest(id: string, reason: string): Promise<RejectionResult> {
    const response = await fetchWithAuth(`${API_BASE}/user-provisioning/requests/${id}/reject`, {
      method: 'POST',

      body: JSON.stringify({ reason }),
    });
    const result = await response.json();
    return result.data;
  },

  /**
   * Cancel a request (as requester)
   */
  async cancelRequest(id: string): Promise<{ status: 'CANCELLED'; message: string }> {
    const response = await fetchWithAuth(`${API_BASE}/user-provisioning/requests/${id}/cancel`, {
      method: 'POST',
    });
    const result = await response.json();
    return result.data;
  },
};
